import { PlatformService } from '@fly/platform-service';
import { basePath, navigateTo, routes, toastService } from '@fly/shared';
import { AxiosError, AxiosInstance } from 'axios';
import axiosRetry from 'axios-retry';
import { t } from 'i18next';
import log from 'loglevel';

export function setupRetryInterceptor(instance: AxiosInstance) {
  const platformService = PlatformService.getInstance();

  return axiosRetry(instance, {
    retries: 3,
    validateResponse: (response) => {
      if (response.data.errors) {
        return false;
      }
      if (response.status >= 200 && response.status < 300) {
        return true;
      }
      return false;
    },
    retryDelay: (retryCount, error) => {
      const delay = retryCount * 1000;
      log.error(
        `Retry attempt #${retryCount}, retry again in ${delay}`,
        error?.message
      );
      return delay;
    },
    retryCondition: (error: AxiosError) => {
      return (
        axiosRetry.isNetworkOrIdempotentRequestError(error) ||
        (error.response?.status ?? 0) >= 500 ||
        !!(
          error.response?.data &&
          typeof error.response.data === 'object' &&
          'errors' in error.response.data
        )
      );
    },

    onMaxRetryTimesExceeded: (error) => {
      const noToast = error.config?.noToast === true;

      if (error.response?.status === 401) {
        platformService.logout();
      }
      if (error.response?.status === 403) {
        if (!noToast) {
          toastService.error(
            t('app.commom.forbidden', { defaultValue: 'Forbidden Access' })
          );
        }
        navigateTo(basePath(routes.DASHBOARD.FORBIDDEN));
      }
      if (error.response?.status && error.response?.status >= 500) {
        if (!noToast) {
          const msg = t('app.common.errors.server_error', {
            defaultValue: 'Server Error',
          });
          toastService.error(msg);
        }
      }
    },

    onRetry: (retryCount, error, config) => {
      if (error.response?.status === 401) {
        platformService.logout();
        throw new Error('Prevent next retry');
      }
      if (error.response?.status === 403) {
        navigateTo(basePath(routes.DASHBOARD.FORBIDDEN));
        throw new Error('Prevent next retry');
      }
    },
  });
}
